import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Factory, Lock, User } from "lucide-react";

export default function Login() {
  const { login, loading } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/input";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    const res = await login(username, password);
    if (res.ok) {
      toast.success("Login berhasil");
      nav(redirectTo);
    } else {
      setErr(res.error);
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-slate-50">
      {/* Hero / visual */}
      <div className="relative hidden md:block bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwyfHxhbHVtaW51bSUyMHNtZWx0ZXIlMjBmYWN0b3J5JTIwaW5kdXN0cmlhbHxlbnwwfHx8fDE3Nzc2MzMyODl8MA&ixlib=rb-4.1.0&q=85"
          alt="Smelter Factory"
          className="absolute inset-0 w-full h-full object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-slate-900/70 to-blue-950/80" />
        <div className="relative z-10 h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <Factory className="w-5 h-5" />
            <span className="text-xs tracking-[0.2em] uppercase font-semibold">Inalum — Rodding Plant</span>
          </div>
          <div>
            <h1 className="text-5xl font-bold tracking-tight leading-tight">
              Rodding<br />Report System
            </h1>
            <p className="mt-4 text-sm max-w-sm text-slate-300">
              Sistem pelaporan shift produksi untuk operasi anode rodding plant. Data tersimpan langsung ke Google Sheets.
            </p>
          </div>
          <div className="text-[11px] tracking-[0.12em] uppercase text-slate-400">
            Control Room — Shift Reporting Terminal
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="md:hidden flex items-center gap-2 mb-8">
            <Factory className="w-6 h-6 text-blue-700" />
            <span className="text-lg font-bold text-slate-900">Rodding Report</span>
          </div>
          <div>
            <button type="button" onClick={() => nav("/dashboard")} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-3" data-testid="btn-back-dashboard">
              <ArrowLeft className="w-3 h-3" /> Kembali ke Dashboard
            </button>
            <div className="label-ind mb-1">Masuk Sistem</div>
            <h2 className="text-2xl font-bold text-slate-900 tracking-tight">Selamat Datang</h2>
            <p className="text-sm text-slate-500 mt-1">Masukkan kredensial untuk melanjutkan.</p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="username" className="label-ind">Username</Label>
              <div className="relative mt-1">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="username"
                  data-testid="input-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="rodding"
                  className="h-10 pl-9 rounded-sm"
                  autoComplete="username"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="label-ind">Password</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  data-testid="input-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-10 pl-9 rounded-sm"
                  autoComplete="current-password"
                  required
                />
              </div>
            </div>
          </div>

          {err && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm px-3 py-2" data-testid="login-error">
              {err}
            </div>
          )}

          <Button
            type="submit"
            data-testid="btn-submit-login"
            disabled={loading}
            className="w-full h-10 bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-semibold tracking-wide"
          >
            {loading ? "Memproses..." : "Masuk"}
          </Button>

          <div className="text-[11px] text-slate-400 text-center tracking-wider uppercase">
            © {new Date().getFullYear()} Rodding Report · Internal System
          </div>
        </form>
      </div>
    </div>
  );
}
